<template>
  <div v-if="visible" class="video-lightbox-overlay" @click="handleOverlayClick">
    <div class="video-lightbox-container">
      <!-- Close button -->
      <button class="video-close-btn" @click="handleClose">
        <vue-feather type="x" size="24" stroke="white"></vue-feather>
      </button>
      
      <!-- Navigation buttons -->
      <button 
        v-if="videos.length > 1" 
        class="video-nav-btn video-prev-btn" 
        @click="prevVideo"
        :disabled="currentIndex === 0"
      >
        <vue-feather type="chevron-left" size="24" stroke="white"></vue-feather>
      </button>
      
      <button 
        v-if="videos.length > 1" 
        class="video-nav-btn video-next-btn" 
        @click="nextVideo"
        :disabled="currentIndex === videos.length - 1"
      >
        <vue-feather type="chevron-right" size="24" stroke="white"></vue-feather>
      </button>
      
      <!-- Video container -->
      <div class="video-content">
        <video 
          v-if="currentVideo"
          :key="currentVideo.id"
          ref="videoPlayer"
          class="video-player"
          controls
          preload="metadata"
          @loadedmetadata="onVideoLoaded"
          @error="onVideoError"
        >
          <source :src="currentVideo.preview_file_name" type="video/mp4">
          <p>Your browser doesn't support HTML5 video.</p>
        </video>
        
        <!-- Video info -->
        <div class="video-info" v-if="currentVideo">
          <p class="video-title">{{ currentVideo.original_file_name }}</p>
          <p class="video-counter">{{ currentIndex + 1 }} / {{ videos.length }}</p>
          
          <!-- Add/Remove buttons -->
          <div class="video-actions">
            <button 
              v-if="currentVideo.atc == 'N'"
              class="video-add-btn"
              @click="addToCart"
            >
              <vue-feather type="plus" size="16" stroke="white"></vue-feather>
              {{ currentLanguage === 'en' ? 'Add' : 'เพิ่ม' }}
            </button>
            
            <button 
              v-if="currentVideo.atc == 'Y'"
              class="video-remove-btn"
              @click="removeFromCart"
            >
              <vue-feather type="minus" size="16" stroke="white"></vue-feather>
              {{ currentLanguage === 'en' ? 'Remove' : 'ลบ' }}
            </button>
          </div>
        </div>
        
        <!-- Loading state -->
        <div v-if="isLoading" class="video-loading">
          <img src="../assets/images/running_loading.svg" style="width: 40px" />
          <p>Loading video...</p>
        </div>
        
        <!-- Error state -->
        <div v-if="hasError" class="video-error">
          <vue-feather type="alert-circle" size="48" stroke="red"></vue-feather>
          <p>Error loading video</p>
          <button @click="retryVideo" class="retry-btn">Retry</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoLightbox',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    videos: {
      type: Array,
      default: () => []
    },
    index: {
      type: Number,
      default: 0
    },
    currentLanguage: {
      type: String,
      default: 'th'
    }
  },
  data() {
    return {
      currentIndex: 0,
      isLoading: false,
      hasError: false
    }
  },
  computed: {
    currentVideo() {
      return this.videos[this.currentIndex] || null;
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.currentIndex = this.index;
        this.isLoading = true;
        this.hasError = false;
        document.body.style.overflow = 'hidden';
        this.$nextTick(() => {
          this.focusVideo();
        });
      } else {
        document.body.style.overflow = '';
        this.pauseVideo();
      }
    },
    index(newVal) {
      if (this.visible) {
        this.currentIndex = newVal;
        this.isLoading = true;
        this.hasError = false;
      }
    },
    currentIndex() {
      this.isLoading = true;
      this.hasError = false;
      this.$emit('index-change', this.currentIndex);
    }
  },
  mounted() {
    // Handle keyboard navigation
    document.addEventListener('keydown', this.handleKeydown);
  },
  
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.body.style.overflow = '';
  },
  
  methods: {
    handleClose() {
      this.pauseVideo();
      this.$emit('hide');
    },
    
    handleOverlayClick(event) {
      if (event.target === event.currentTarget) {
        this.handleClose();
      }
    },
    
    prevVideo() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      }
    },
    
    nextVideo() {
      if (this.currentIndex < this.videos.length - 1) {
        this.currentIndex++;
      }
    },
    
    onVideoLoaded() {
      this.isLoading = false;
      this.hasError = false;
    },
    
    onVideoError() {
      this.isLoading = false;
      this.hasError = true;
    },
    
    retryVideo() {
      this.hasError = false;
      this.isLoading = true;
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.load();
      }
    },
    
    pauseVideo() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause();
      }
    },
    
    focusVideo() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.focus();
      }
    },
    
    addToCart() {
      this.$emit('add-to-cart', {
        video: this.currentVideo,
        index: this.currentIndex
      });
    },
    
    removeFromCart() {
      this.$emit('remove-from-cart', {
        video: this.currentVideo,
        index: this.currentIndex
      });
    },
    
    handleKeydown(event) {
      if (!this.visible) return;
      
      switch (event.key) {
        case 'Escape':
          this.handleClose();
          break;
        case 'ArrowLeft':
          this.prevVideo();
          break;
        case 'ArrowRight':
          this.nextVideo();
          break;
        case ' ':
          event.preventDefault();
          if (this.$refs.videoPlayer) {
            if (this.$refs.videoPlayer.paused) {
              this.$refs.videoPlayer.play();
            } else {
              this.$refs.videoPlayer.pause();
            }
          }
          break;
      }
    }
  }
}
</script>

<style scoped>
.video-lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  overflow: hidden;
}

.video-lightbox-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.video-content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
  max-height: 100%;
}

.video-player {
  max-width: 90vw;
  max-height: 75vh;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.video-info {
  margin-top: 15px;
  text-align: center;
  color: white;
}

.video-title {
  font-size: 16px;
  font-weight: bold;
  margin: 0 0 5px 0;
  max-width: 90vw;
  word-break: break-word;
}

.video-counter {
  font-size: 14px;
  opacity: 0.8;
  margin: 0;
}

.video-close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10001;
  transition: background-color 0.2s;
}

.video-close-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

.video-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10001;
  transition: background-color 0.2s, opacity 0.2s;
}

.video-nav-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.8);
}

.video-nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.video-prev-btn {
  left: 20px;
}

.video-next-btn {
  right: 20px;
}

.video-loading,
.video-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  min-height: 300px;
  min-width: 400px;
}

.video-loading p,
.video-error p {
  margin: 10px 0;
  font-size: 16px;
}

.retry-btn {
  margin-top: 10px;
  padding: 8px 16px;
  background: #0486d6;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.retry-btn:hover {
  background: #0367a6;
}

/* Video action buttons */
.video-actions {
  margin-top: 15px;
  display: flex;
  gap: 10px;
  justify-content: center;
}

.video-add-btn,
.video-remove-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s, transform 0.1s;
}

.video-add-btn {
  background: #0486d6;
  color: white;
}

.video-add-btn:hover {
  background: #0367a6;
  transform: translateY(-1px);
}

.video-remove-btn {
  background: #ff1c1c;
  color: white;
}

.video-remove-btn:hover {
  background: #d61717;
  transform: translateY(-1px);
}

.video-add-btn:active,
.video-remove-btn:active {
  transform: translateY(0);
}

/* Mobile responsive */
@media screen and (max-width: 768px) {
  .video-lightbox-container {
    max-width: 95vw;
    max-height: 95vh;
  }
  
  .video-player {
    max-width: 95vw;
    max-height: 70vh;
  }
  
  .video-close-btn {
    top: 10px;
    right: 10px;
    width: 35px;
    height: 35px;
  }
  
  .video-nav-btn {
    width: 40px;
    height: 40px;
  }
  
  .video-prev-btn {
    left: 10px;
  }
  
  .video-next-btn {
    right: 10px;
  }
  
  .video-title {
    font-size: 14px;
    max-width: 95vw;
  }
  
  .video-counter {
    font-size: 12px;
  }
  
  .video-loading,
  .video-error {
    min-width: 300px;
    min-height: 250px;
  }
}

/* Very small screens */
@media screen and (max-width: 480px) {
  .video-player {
    max-width: 98vw;
    max-height: 65vh;
  }
  
  .video-nav-btn {
    width: 35px;
    height: 35px;
  }
  
  .video-loading,
  .video-error {
    min-width: 250px;
    min-height: 200px;
  }
}
</style>